import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState
} from 'react'

export type ProjectAutofillDraftGuardApi = {
  isDirty: () => boolean
  canPersist: () => boolean
  saveDraft: () => Promise<boolean>
  canPublish?: () => boolean
  publishDraft?: () => Promise<void>
  isPublishing?: () => boolean
}

type ProjectAutofillDraftGuardContextValue = {
  setProjectAutofillDraftGuardApi: (api: ProjectAutofillDraftGuardApi | null) => void
  getProjectAutofillDraftGuardApi: () => ProjectAutofillDraftGuardApi | null
  draftUiTick: number
  bumpDraftUi: () => void
}

const ProjectAutofillDraftGuardContext =
  createContext<ProjectAutofillDraftGuardContextValue | null>(null)

export function ProjectAutofillDraftGuardProvider({ children }: { children: React.ReactNode }) {
  const draftGuardApiRef = useRef<ProjectAutofillDraftGuardApi | null>(null)
  const [draftUiTick, setDraftUiTick] = useState(0)

  const bumpDraftUi = useCallback(() => {
    setDraftUiTick((n) => n + 1)
  }, [])

  const setProjectAutofillDraftGuardApi = useCallback((api: ProjectAutofillDraftGuardApi | null) => {
    draftGuardApiRef.current = api
  }, [])

  const getProjectAutofillDraftGuardApi = useCallback(() => draftGuardApiRef.current, [])

  const value = useMemo(
    () => ({
      setProjectAutofillDraftGuardApi,
      getProjectAutofillDraftGuardApi,
      draftUiTick,
      bumpDraftUi
    }),
    [setProjectAutofillDraftGuardApi, getProjectAutofillDraftGuardApi, draftUiTick, bumpDraftUi]
  )

  return (
    <ProjectAutofillDraftGuardContext.Provider value={value}>
      {children}
    </ProjectAutofillDraftGuardContext.Provider>
  )
}

export function useProjectAutofillDraftGuard(): ProjectAutofillDraftGuardContextValue {
  const v = useContext(ProjectAutofillDraftGuardContext)
  if (!v) {
    throw new Error('useProjectAutofillDraftGuard must be used within ProjectAutofillDraftGuardProvider')
  }
  return v
}

export function useProjectAutofillDraftGuardOptional(): ProjectAutofillDraftGuardContextValue | null {
  return useContext(ProjectAutofillDraftGuardContext)
}
